import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  GridColumnV2,
  GridFilterParamV2,
  GridStateChangeV2,
  GridStateV2,
  GridUtilsV2,
  StorageV2Service,
  StoreV2,
  User,
} from '@schenck/rotec-components';

import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { selectGridConfig, selectTypeData } from './store/rotor-type-list.selectors';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { GridConfig } from 'src/utils/interfaces/rotor-type.interfaces';
import { loadTypeData, updateGridConfig } from './store/rotor-type-list.actions';
import { isEqual } from 'lodash';

const serviceName = 'RotorTypeListService';

export enum RotorTypeKeys {
  name = 'name',
  machineName = 'machineName',
  status = 'status',
  syncState = 'syncState',
  createdBy = 'createdBy',
  created = 'created',
  modified = 'modified',
  modifiedBy = 'modifiedBy',
}

const initialState = {
  filter: [],
  fulltext: '',
  page: 0,
  sort: [],
  total: 0,
  results: [],
  pageSize: 0,
  type: '',
};

export const GRID_CONFIG_DEFAULT: GridConfig = {
  pageSize: 15,
  displayedColumns: [
    RotorTypeKeys.name,
    RotorTypeKeys.machineName,
    RotorTypeKeys.status,
    RotorTypeKeys.syncState,
    RotorTypeKeys.createdBy,
    RotorTypeKeys.created,
    RotorTypeKeys.modifiedBy,
    RotorTypeKeys.modified,
  ],
  columnOrder: [
    RotorTypeKeys.name,
    RotorTypeKeys.machineName,
    RotorTypeKeys.status,
    RotorTypeKeys.syncState,
    RotorTypeKeys.createdBy,
    RotorTypeKeys.created,
    RotorTypeKeys.modifiedBy,
    RotorTypeKeys.modified,
  ],
  columnsWidths: {
    [RotorTypeKeys.name]: 170,
    [RotorTypeKeys.machineName]: 170,
    [RotorTypeKeys.status]: 170,
    [RotorTypeKeys.syncState]: 170,
    [RotorTypeKeys.createdBy]: 170,
    [RotorTypeKeys.created]: 170,
    [RotorTypeKeys.modifiedBy]: 170,
    [RotorTypeKeys.modified]: 170,
  },
};

@Injectable()
export class RotorTypeListService extends StoreV2<GridStateV2> implements OnDestroy {
  private readonly store = inject<Store<GridStateV2>>(Store);
  private readonly storageService: StorageV2Service;

  public defaultFilters = {
    [RotorTypeKeys.name]: GridUtilsV2.multiselectFilter,
    [RotorTypeKeys.machineName]: GridUtilsV2.multiselectFilter,
    [RotorTypeKeys.status]: GridUtilsV2.checkboxFilter,
    [RotorTypeKeys.syncState]: GridUtilsV2.checkboxFilter,
    [RotorTypeKeys.createdBy]: GridUtilsV2.checkboxFilter,
    [RotorTypeKeys.created]: GridUtilsV2.dateRangeFilter,
    [RotorTypeKeys.modifiedBy]: GridUtilsV2.checkboxFilter,
    [RotorTypeKeys.modified]: GridUtilsV2.dateRangeFilter,
  };

  gridConfig: GridConfig = {
    pageSize: 0,
    displayedColumns: [],
    columnOrder: [],
    columnsWidths: {},
  };
  gridConfig$: BehaviorSubject<GridConfig> = new BehaviorSubject(this.gridConfig);

  private rotorTypeParams$: BehaviorSubject<GridStateChangeV2> = new BehaviorSubject({
    page: this.state.page,
    filter: this.state.filter,
    sort: this.state.sort,
    fulltext: this.state.fulltext,
    type: this.state.type,
  });

  private lastSearchValue = null;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public get gridFilter(): GridFilterParamV2[] {
    return this.state.fulltext
      ? [...this.state.filter, { key: 'RotorTypeName', value: [this.state.fulltext] }]
      : this.state.filter;
  }

  constructor() {
    const storageService = inject(StorageV2Service);

    super(initialState, serviceName, { name: serviceName, storageService });
    this.storageService = storageService;

    this.store
      .pipe(
        select(selectGridConfig),
        takeUntil(this.destroyed$),
        filter((gridConfig: GridConfig) => !!gridConfig),
        tap((gridConfig) => {
          const pageSizeChanged = gridConfig.pageSize !== this.gridConfig.pageSize;

          this.gridConfig = gridConfig;
          this.gridConfig$.next(gridConfig);

          if (pageSizeChanged) {
            this.store.dispatch(
              loadTypeData({
                filter: this.gridFilter,
                limit: gridConfig.pageSize.toString(),
                page: (this.state.page + 1).toString(),
              })
            );
          }
        })
      )
      .subscribe();

    combineLatest([
      this.store.pipe(
        select(selectTypeData),
        takeUntil(this.destroyed$),
        filter((typeData) => !!typeData)
      ),
      this.rotorTypeParams$,
    ])
      .pipe(
        tap(([typeData, rotorTypeParams]) => {
          const results = typeData.data.map((result) => {
            return {
              ...result,
              createdBy: this.getUserName(result.createdBy as User),
              modifiedBy: this.getUserName(result.modifiedBy as User),
            };
          });

          const page =
            typeof rotorTypeParams.page === 'number' ? rotorTypeParams.page : typeData.pageNumber;

          this.patchState(`${rotorTypeParams.type}`, {
            ...rotorTypeParams,
            ...GridUtilsV2.getResults(results, {
              ...rotorTypeParams,
              page,
              filterFnMap: this.defaultFilters,
              resultComparator: GridUtilsV2.defaultResultComparator,
            }),
            fulltext: rotorTypeParams.fulltext,
            filter: rotorTypeParams.filter,
            page,
            pageSize: typeData.pageSize,
            total: typeData.totalRecords,
          });
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public reactToStateChange(event: GridStateChangeV2) {
    if (['filter', 'deleteFilter', 'pageChanged'].includes(event.type)) {
      const gridFilter = this.state.fulltext
        ? [...event.filter, { key: 'RotorTypeName', value: [this.state.fulltext] }]
        : event.filter;

      this.store.dispatch(
        loadTypeData({
          filter: gridFilter,
          page: (event.page + 1).toString(),
          limit: this.state.pageSize.toString(),
        })
      );
    }
    this.rotorTypeParams$.next(event);
  }

  // When the service is initialized there is no info about the current user yet.
  // So that it's impossible to take the data from local storage by default flow.
  // Therefore when the user data is loaded the method should be called in order to
  // get the persisted data from the storage correctly
  public setGridState(): void {
    const { page, filter, sort, fulltext, type } =
      this.storageService.getItem(serviceName) || initialState;

    this.rotorTypeParams$.next({ page, filter, sort, fulltext, type });
    this.patchState('Storage params', { page, filter, sort, fulltext, type });
  }

  public reactToPageSizeChange(pageSize: number) {
    if (this.gridConfig.pageSize !== pageSize) {
      const gridConfig = { ...this.gridConfig, pageSize };
      const totalPages = !pageSize ? 0 : Math.ceil(this.state.total / pageSize);
      this.rotorTypeParams$.next({
        ...this.rotorTypeParams$.value,
        page: Math.min(this.state.page, totalPages - 1),
      });
      this.store.dispatch(updateGridConfig(gridConfig));
    }
  }

  public reactToColumnsChange(event: GridColumnV2[]) {
    const displayedColumns = [];
    const columnOrder = [];
    const columnsWidths = {};

    event.forEach((column) => {
      columnOrder.push(column.key);
      columnsWidths[column.key] = parseFloat(column.width.toString());
      if (!column.invisible) {
        displayedColumns.push(column.key);
      }
    });

    const gridConfig = {
      ...this.gridConfig,
      displayedColumns,
      columnOrder,
      columnsWidths,
    };

    if (!isEqual(gridConfig, this.gridConfig)) {
      this.gridConfig = gridConfig;
      this.store.dispatch(updateGridConfig(this.gridConfig));
    }
  }

  public fulltextSearchChanged(search: string) {
    if (search !== this.lastSearchValue) {
      this.lastSearchValue = search;

      const gridFilter = search
        ? [...this.state.filter, { key: 'RotorTypeName', value: [search] }]
        : this.state.filter;

      this.store.dispatch(
        loadTypeData({
          filter: gridFilter,
          limit: this.state.pageSize.toString(),
          page: '1',
        })
      );

      this.rotorTypeParams$.next({
        ...this.rotorTypeParams$.value,
        page: 0,
        type: 'fulltext',
        fulltext: search,
      });
    }
  }

  public showFirstPage() {
    this.rotorTypeParams$.next({
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: 0,
      sort: this.state.sort,
      type: 'pageChange',
    });
  }

  public setInitialGridConfig(): void {
    this.gridConfig = {
      pageSize: 0,
      displayedColumns: [],
      columnOrder: [],
      columnsWidths: {},
    };
  }

  public setSyncState(id, syncState) {
    if (this.state.results.find((item) => item.id === id)) {
      const updatedResults = this.state.results.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            syncState: syncState,
          };
        }
        return item;
      });
      this.patchState('SyncState', {
        ...this.state,
        results: updatedResults,
      });
    }
  }

  private getUserName(user: User) {
    return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '';
  }
}
